import React from 'react'
import { Col } from 'react-bootstrap'
import { useQuery } from '@apollo/client'
import Loader from 'components/ui/loader/Loader'

import { COURSE, TDataCourse, QueryCourseAllArgs } from 'graphqlAPI'
import { useTranslationRankmi } from 'helpers/translations.helper'
import Progress from 'components/monitoring/components/Progress'
import { getProgressPercentage } from 'components/monitoring/utils'
import { useAnimatedNumbers } from 'hooks/useAnimatedNumbers'
import { Cards } from '../Cards'
import cardStyles from '../Cards/Cards.module.scss'

import { prefix } from '../constants'
import { RolesAccessBanner } from '../RolesAccessBanner'
import styles from './CourseProgress.module.scss'

interface CourseProgressProps {
  courseId: string
  areaId?: string | number
}

export const CourseProgress: React.FC<CourseProgressProps> = ({
  courseId,
  areaId,
}) => {
  const { data, loading } = useQuery<TDataCourse, QueryCourseAllArgs>(COURSE, {
    variables: { id: courseId, areaId: areaId?.toString() },
  })

  const course = data?.course

  const progress = React.useMemo(() => {
    return {
      finish: 0,
      inProgress: 0,
      pending: 0,
      ...course?.progress,
    }
  }, [course?.progress])

  const { finish, inProgress, pending } = getProgressPercentage([progress])

  const animatedProgress = useAnimatedNumbers({ finish, inProgress, pending })

  const animatedCounts = useAnimatedNumbers({
    participants: course?.participantsCount ?? 0,
    finish: progress.finish,
    inProgress: progress.inProgress,
    pending: progress.pending,
  })

  const tRelative = useTranslationRankmi({ prefix: prefix })

  if (loading) {
    return (
      <>
        <Col xs={12}>
          <RolesAccessBanner />
        </Col>
        <Col>
          <div className="d-flex justify-content-center">
            <Loader color />
          </div>
        </Col>
      </>
    )
  }

  if (!course) return null

  const {
    participants: animatedParticipants,
    finish: animatedFinish,
    inProgress: animatedInProgress,
    pending: animatedPending,
  } = animatedCounts

  return (
    <>
      <Col xs={12}>
        <RolesAccessBanner />
      </Col>
      <Col xs={12} className={styles.progressCol}>
        <div className={styles.progressRow}>
          <div className={styles.progressSection}>
            <span className={styles.progressLabel}>
              {tRelative('progress')}
            </span>
            <Progress
              finish={animatedProgress.finish}
              inProgress={animatedProgress.inProgress}
              pending={animatedProgress.pending}
              className={styles.progressBar}
              classes={{
                finish: styles.progressFinish,
                inProgress: styles.progressInProgress,
                pending: styles.progressPending,
              }}
            />
          </div>
          <div className={styles.cardsGroup}>
            <Cards
              title={tRelative('cards.participants')}
              count={animatedParticipants}
            />
            <Cards
              title={tRelative('cards.finished')}
              count={animatedFinish}
              total={animatedParticipants}
              classes={{ card: cardStyles.finish }}
            />
            <Cards
              title={tRelative('cards.inProgress')}
              count={animatedInProgress}
              total={animatedParticipants}
              classes={{ card: cardStyles.inProcess }}
            />
            <Cards
              title={tRelative('cards.pending')}
              count={animatedPending}
              total={animatedParticipants}
              classes={{ card: cardStyles.waiting }}
            />
          </div>
        </div>
      </Col>
    </>
  )
}
